<template>
	<div class="netlink-export-wrapper">
		<vs-popup
			:active.sync="active"
			title="Exporter les budgets Netlinking"
			:fullscreen="false"
			class="netlink-export-popup"
			style="z-index: 9999999 !important"
		>
			<div class="netlink-export-container">
				<p class="export-intro">
					Une ligne par prestation et par période, avec le budget, les reports
					et le consommé de chaque mois.
				</p>

				<div class="period-pickers">
					<div class="status-filter">
						<label>Statut</label>
						<vs-select v-model="statusFilter" class="w-full select-large">
							<vs-select-item
								v-for="option in statusOptions"
								:key="option.id"
								:value="option.id"
								:text="option.name"
								class="w-full"
							/>
						</vs-select>
					</div>
					<div class="period-picker">
						<label>Période de début</label>
						<month-picker-input
							:max-date="maxDate"
							:default-year="defaultYear"
							:default-month="defaultMonth"
							:no-default="false"
							lang="fr"
							@input="setStartPeriod"
						/>
					</div>
					<div class="period-picker">
						<label>Période de fin</label>
						<month-picker-input
							:max-date="maxDate"
							:default-year="defaultYear"
							:default-month="defaultMonth"
							:no-default="false"
							lang="fr"
							@input="setEndPeriod"
						/>
					</div>
				</div>

				<p v-if="rangeInvalid" class="range-error">
					La période de début doit précéder la période de fin.
				</p>
				<p v-else class="range-summary">
					{{ periodsInRange.length }} période(s) :
					{{ startPeriod }} &rarr; {{ endPeriod }}
				</p>

				<div class="export-actions">
					<vs-button
						color="dark"
						type="flat"
						:disabled="isExporting"
						@click="active = false"
					>
						Annuler
					</vs-button>
					<vs-button
						color="primary"
						type="filled"
						icon-pack="feather"
						icon="icon-download-cloud"
						:disabled="isExporting || rangeInvalid"
						@click="exportBudgets()"
					>
						{{
							isExporting
								? `Export en cours… (${progressDone}/${progressTotal})`
								: "Exporter"
						}}
					</vs-button>
				</div>
			</div>
		</vs-popup>
	</div>
</template>

<script>
import moment from "moment";
import XLSX from "xlsx";
import { MonthPickerInput } from "vue-month-picker";
import dbFieldConvertor from "@/mixins/dbFieldConvertorMixin";
import Netlink from "../../entities/Netlink";

/**
 * Period-range export of the live budget recap ("Gestion Netlinking").
 *
 * Reuses POST /netlink/getLiveBudgetRecap one month at a time - the same
 * endpoint the table reads - so the exported figures can never diverge from
 * what the popup displays. One xlsx row per prestation per period.
 */
export default {
	name: "NetlinksBudgetExportPopup",
	mixins: [dbFieldConvertor],
	components: {
		MonthPickerInput,
	},
	props: {
		popupActive: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			active: false,
			// Both pickers default to the current period; the user usually only
			// moves the start back.
			startPeriod: moment().format("MM/YYYY"),
			endPeriod: moment().format("MM/YYYY"),
			maxDate: new Date(),
			defaultYear: moment().year(),
			defaultMonth: moment().month() + 1,
			isExporting: false,
			progressDone: 0,
			progressTotal: 0,
			// Same options as the Gestion Netlinking Statut filter (Annulé is
			// excluded server-side there too), plus "Tous" to export everything.
			// Filters on today's contract status, like the popup's own filter.
			statusFilter: 1,
			statusOptions: [
				{ name: "Tous", id: "" },
				{ name: "Actif", id: 1 },
				{ name: "A Valider", id: 2 },
				{ name: "A venir", id: 8 },
				{ name: "En pause", id: 3 },
				{ name: "Terminé", id: 4 },
				{ name: "Relance", id: 9 },
			],
		};
	},
	computed: {
		rangeInvalid() {
			return moment(this.startPeriod, "MM/YYYY").isAfter(
				moment(this.endPeriod, "MM/YYYY"),
				"month"
			);
		},
		periodsInRange() {
			if (this.rangeInvalid) return [];
			const end = moment(this.endPeriod, "MM/YYYY");
			const cursor = moment(this.startPeriod, "MM/YYYY");
			const periods = [];
			while (cursor.isSameOrBefore(end, "month")) {
				periods.push(cursor.format("MM/YYYY"));
				cursor.add(1, "month");
			}
			return periods;
		},
	},
	watch: {
		popupActive(value) {
			this.active = value;
		},
		active(value) {
			this.$emit("update:popupActive", value);
		},
	},
	mounted() {
		/*
		 * Move the popup to <body>. It opens on top of the fullscreen "Gestion
		 * Netlinking" popup, and while it stays nested inside that popup's DOM no
		 * z-index reliably lifts it above the dialog and its backdrop.
		 * CorrectionNotePopup does the same for the same clash.
		 */
		this.$nextTick(() => {
			if (this.$el && document.body) {
				document.body.appendChild(this.$el);
			}
		});
	},
	beforeDestroy() {
		if (this.$el && this.$el.parentNode) {
			this.$el.parentNode.removeChild(this.$el);
		}
	},
	methods: {
		/* vue-month-picker emits {from, to, month, monthIndex, year}. */
		setStartPeriod(date) {
			this.startPeriod = moment(date.from).format("MM/YYYY");
		},
		setEndPeriod(date) {
			this.endPeriod = moment(date.from).format("MM/YYYY");
		},

		async exportBudgets() {
			const periods = this.periodsInRange;
			if (periods.length === 0) return;

			this.isExporting = true;
			this.progressDone = 0;
			this.progressTotal = periods.length;

			const toNumber = (value) => parseFloat(value) || 0;
			const rows = [];

			try {
				/*
				 * Sequential on purpose: each call replays every prestation month by
				 * month server-side, so firing the whole range at once would pile up
				 * heavy queries.
				 */
				for (const period of periods) {
					const response = await Netlink.getLiveBudgetRecap({ period });
					this.progressDone++;

					// Before the budget epoch nothing exists - skip, don't fabricate.
					if (response.before_epoch) continue;

					(response.contracts || []).forEach((contract) => {
						if (
							this.statusFilter !== "" &&
							String(contract.contract_status) !== String(this.statusFilter)
						) {
							return;
						}
						(contract.items || []).forEach((mission) => {
							rows.push([
								contract.society_name || "",
								contract.id,
								this.getContractStatus(contract.contract_status) || "",
								mission.mission_consultant_name ||
									contract.consultant_name ||
									"",
								mission.mission_name || "",
								mission.mission_id,
								period,
								Number(mission.recurrence) === 0 ? "One shot" : "Mensuel",
								toNumber(mission.display_budget),
								toNumber(mission.last_budget_rest),
								toNumber(mission.corrected_budget),
								toNumber(mission.total_budget),
								toNumber(mission.total_sold_price),
							]);
						});
					});
				}

				const header = [
					"Client",
					"ID Contrat",
					"Statut",
					"Consultant",
					"Prestation",
					"N° Prestation",
					"Période",
					"Récurrence",
					"Budget Période / Oneshot",
					"Restant antérieur",
					"Correction antérieur",
					"Total Budget",
					"Consommé Période",
				];

				const worksheet = XLSX.utils.aoa_to_sheet([header, ...rows]);
				const workbook = XLSX.utils.book_new();
				XLSX.utils.book_append_sheet(workbook, worksheet, "Budgets Netlinking");

				const fileName =
					"Budgets_Netlinking_" +
					periods[0].replace("/", "-") +
					"_" +
					periods[periods.length - 1].replace("/", "-") +
					".xlsx";
				XLSX.writeFile(workbook, fileName);

				this.$vs.notify({
					title: "Succès",
					text: `${rows.length} ligne(s) exportée(s)`,
					color: "success",
				});
				this.active = false;
			} catch (error) {
				console.error("Error exporting netlink budgets:", error);
				this.$vs.notify({
					title: "Erreur",
					text: "Erreur lors de l'export des budgets",
					color: "danger",
				});
			} finally {
				this.isExporting = false;
			}
		},
	},
};
</script>

<style>
/*
 * Unscoped on purpose: Vuesax renders .vs-popup and .vs-popup--background
 * itself, and a scoped rule is rewritten with a data attribute those internal
 * nodes do not carry, so it would never reach them.
 */
.netlink-export-wrapper {
	position: fixed !important;
	top: 0 !important;
	left: 0 !important;
	width: 100% !important;
	height: 100% !important;
	pointer-events: none !important;
	z-index: 9999999 !important;
}

.netlink-export-wrapper > * {
	pointer-events: auto !important;
}

.netlink-export-wrapper .vs-popup {
	z-index: 9999999 !important;
}

.netlink-export-wrapper .vs-popup--background {
	z-index: 9999998 !important;
}

/* The month-picker panel opens below its input; the popup must not clip it. */
.netlink-export-wrapper .vs-popup__content,
.netlink-export-wrapper .vs-popup--content {
	z-index: 9999999 !important;
	overflow: visible !important;
}

/*
 * Vuesax relocates every vs-select option list to <body> with z-index 40000,
 * which puts it BEHIND this popup (9999999) - the list opens but is invisible.
 * The panel carries no class tying it to its select, so this has to be global;
 * an open dropdown list belongs on top of everything anyway.
 */
.vs-select--options {
	z-index: 10000000 !important;
}
</style>

<style scoped>
.netlink-export-container {
	padding: 20px;
	min-width: 680px;
	/* Tall enough for the month-picker panel to open fully inside the popup */
	min-height: 460px;
	display: flex;
	flex-direction: column;
}

.netlink-export-container .export-actions {
	margin-top: auto;
}

.export-intro {
	margin: 0 0 1rem 0;
	font-size: 0.9rem;
	color: #626262;
}

.period-pickers {
	display: flex;
	align-items: flex-end;
	gap: 0.75rem;
}

.status-filter {
	flex: 0 0 160px;
}

.status-filter label {
	display: block;
	margin-bottom: 0.25rem;
	font-size: 0.8rem;
	color: #888;
}

/* Size to the picker itself so début and fin sit right next to each other */
.period-picker {
	flex: 0 0 auto;
}

.period-picker label {
	display: block;
	margin-bottom: 0.25rem;
	font-size: 0.8rem;
	color: #888;
}

.range-error {
	margin: 0.75rem 0 0 0;
	font-size: 0.85rem;
	color: #d32f2f;
	font-weight: bold;
}

.range-summary {
	margin: 0.75rem 0 0 0;
	font-size: 0.85rem;
	color: #888;
}

.export-actions {
	display: flex;
	justify-content: flex-end;
	gap: 0.5rem;
	margin-top: 1.25rem;
}
</style>
